import React, { useCallback } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import {
  Button,
  Card,
  DialogOverlay,
  DialogOverlayActions,
  H2,
  IconButton,
  Image,
  Paragraph,
  Row,
  Separator,
} from '@fs/zion-ui'
import { Arrow, NoticeInfo } from '@fs/zion-icon'
import { useNavigate } from '../../../hooks'

// #region Styles
const cssPreviewCardContentWrapper = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`
const cssFlexSpacer = css`
  flex: 1;
`
// #endregion

/**
 * @describe - Card that shows how leaders, helpers, and members can start using FNA.
 */
export default function PreviewCard({
  description,
  descriptionMinHeight,
  heading,
  headingMinHeight,
  linkDropSearch = false,
  linkExternal = false,
  linkName,
  linkSearch,
  linkUrl,
  onNext,
  overlay,
  rootTestId,
  overlayTitle,
  overlayText,
  showOverlay,
  svgImage,
}) {
  const [t] = useTranslation()
  const { onPush } = useNavigate()

  const onNextClick = useCallback(() => {
    onNext?.()
    onPush(linkUrl, linkName, linkDropSearch, linkExternal, linkSearch)
  }, [linkDropSearch, linkExternal, linkName, linkSearch, linkUrl, onNext, onPush])

  return (
    <>
      <Card margins="sparse" maxWidth={364}>
        <div css={cssPreviewCardContentWrapper}>
          <Separator size="xs" />
          <div style={{ minHeight: `${headingMinHeight}px` }}>
            <H2 centered size="H5">
              {heading}
            </H2>
          </div>
          <Separator size="sm" />
          <div style={{ minHeight: `${descriptionMinHeight}px` }}>
            <Row>
              <Paragraph size="sm">{description}</Paragraph>
              {showOverlay && (
                <IconButton
                  data-testid={`${rootTestId}.info-overlay-button`}
                  description={overlayTitle}
                  Icon={NoticeInfo}
                  onClick={overlay.handleClick}
                />
              )}
            </Row>
          </div>
          <Separator size={descriptionMinHeight ? 'sm' : 'lg'} />
          <Image height={150} noCrop src={svgImage} alt="" />
          <div css={cssFlexSpacer} />
          <Separator size="lg" />
          <Button centered data-testid={`${rootTestId}.next-button`} emphasis="medium" onClick={onNextClick}>
            {t('common-ui:next.action', 'Next')}
          </Button>
        </div>
      </Card>

      {overlay?.isOpen && (
        <DialogOverlay
          {...overlay}
          footer={
            <DialogOverlayActions>
              <Button data-testid={`${rootTestId}.overlay-cancel-button`} onClick={overlay.close}>
                {t('common-ui:cancel.action', 'Cancel')}
              </Button>
              <Button
                data-testid={`${rootTestId}.overlay-next-button`}
                emphasis="high"
                Icon={Arrow}
                iconAtEnd
                onClick={onNextClick}
              >
                {t('common-ui:next.action', 'Next')}
              </Button>
            </DialogOverlayActions>
          }
          title={overlayTitle}
        >
          {overlayText}
        </DialogOverlay>
      )}
    </>
  )
}
PreviewCard.propTypes = {
  description: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  linkExternal: PropTypes.bool,
  linkName: PropTypes.string.isRequired,
  linkSearch: PropTypes.string,
  linkUrl: PropTypes.string.isRequired,
  svgImage: PropTypes.string.isRequired,
}
